import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shareToWhatsApp } from "@/lib/share";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Mail, Globe, Loader2 } from "lucide-react";

/**
 * Order a selected subset of a request's items from one supplier via a channel.
 * Props: open, onOpenChange, request, selectedIndexes (number[]), onDone?
 */
export default function OrderItemsDialog({ open, onOpenChange, request, selectedIndexes = [], onDone }) {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/suppliers");
        setSuppliers(data);
      } catch (_) {}
    })();
    setSupplierId(request?.supplier_id || "");
  }, [open, request]);

  const items = request?.items || [];
  const selItems = useMemo(
    () => selectedIndexes.map((i) => items[i]).filter(Boolean),
    [selectedIndexes, items]
  );
  const supplier = suppliers.find((s) => s.id === supplierId);

  // For a line with a pending alternative, the order is placed for the ALTERNATIVE.
  const eff = (it) => {
    const a = it.alternative;
    if (a && !a.ordered) {
      return { code: a.product_code, name: a.product_name, quantity: a.quantity, unit: a.unit, note: it.note, isAlt: true, origCode: it.product_code };
    }
    return { code: it.product_code, name: it.product_name, quantity: it.quantity, unit: it.unit, note: it.note, isAlt: false };
  };

  const buildItemList = () =>
    selItems
      .map((it, i) => {
        const e = eff(it);
        return `${i + 1}. ${e.code} — ${e.name} • ${e.quantity} ${e.unit}${e.isAlt ? ` (alternatif · asıl: ${e.origCode})` : ""}${e.note ? `\n   Not: ${e.note}` : ""}`;
      })
      .join("\n");

  const fetchPartialPdf = async () => {
    const sid = supplierId || request?.supplier_id || "";
    const { data } = await api.get(`/requests/${request.id}/pdf?indexes=${selectedIndexes.join(",")}${sid ? `&supplier_id=${sid}` : ""}`, { responseType: "blob" });
    return new File([data], `talep-${request.request_no}.pdf`, { type: "application/pdf" });
  };

  const openWhatsApp = async (supName, supPhone) => {
    const waNumber = (supPhone || "").replace(/[^\d]/g, "");
    const message = `Merhaba ${supName},\n\n*${request.request_no}* numaralı sipariş talebimiz:\n${buildItemList()}\n\nDetaylar ekteki PDF'tedir.`;
    const waUrl = waNumber
      ? `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    let file = null;
    try { file = await fetchPartialPdf(); } catch (_) { /* text only */ }
    await shareToWhatsApp({
      text: message,
      title: `Talep ${request.request_no}`,
      file,
      waUrl,
      downloadName: `talep-${request.request_no}.pdf`,
    });
  };

  const openMail = async (supName, supEmail) => {
    // Download the partial PDF so the user can attach it
    try {
      const file = await fetchPartialPdf();
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (_) {}
    const subject = `Sipariş Talebi: ${request.request_no}`;
    const body =
      `Merhaba ${supName},\n\n` +
      `${request.request_no} numaralı talebimizdeki ürünler (ekteki PDF):\n${buildItemList()}\n\n` +
      `Bilgilerinize sunarız, iyi çalışmalar dileriz.\n\n` +
      `Talep Eden: ${user?.name || ""}` + (user?.email ? ` <${user.email}>` : "");
    const params = new URLSearchParams();
    params.set("subject", subject);
    params.set("body", body);
    if (user?.email) params.set("cc", user.email);
    const mailUrl = `mailto:${encodeURIComponent(supEmail || "")}?${params.toString().replace(/\+/g, "%20")}`;
    const link = document.createElement("a");
    link.href = mailUrl;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const submit = async (channel) => {
    if (!supplierId) { toast.error("Lütfen tedarikçi seçin"); return; }
    if (selItems.length === 0) { toast.error("Ürün seçilmedi"); return; }
    const supName = supplier?.name || "";
    // Open the native share sheet / mail client first (within the gesture) so
    // navigator.share keeps activation; recording the order follows.
    if (channel === "whatsapp") await openWhatsApp(supName, supplier?.phone);
    else if (channel === "mail") await openMail(supName, supplier?.email);
    setBusy(channel);
    try {
      await api.post(`/requests/${request.id}/order-items`, {
        item_indexes: selectedIndexes,
        supplier_id: supplierId,
        channel,
      });
      const label = { whatsapp: "WhatsApp", mail: "Mail", b2b: "B2B" }[channel];
      toast.success(`${selItems.length} ürün ${supName} tedarikçisine sipariş verildi (${label})`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="order-items-dialog" className="max-w-md p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Globe className="h-5 w-5 text-indigo-600" /> Seçili Ürünleri Sipariş Ver
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            <span className="font-semibold text-slate-700">{selItems.length}</span> ürün için tedarikçi seçin ve kanal ile gönderin. Sadece seçili ürünler gönderilir, kalanlar beklemede kalır.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="order-supplier-select" className="mt-1.5 h-11">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`order-supplier-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-slate-100 rounded-md overflow-hidden max-h-44 overflow-y-auto thin-scroll">
            {selItems.map((it, idx) => {
              const e = eff(it);
              return (
              <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 first:border-t-0 items-center text-xs">
                <div className="col-span-4 font-medium text-slate-900">
                  {e.code}
                  {e.isAlt && <span className="ml-1 text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 align-middle">Alt</span>}
                </div>
                <div className="col-span-6 text-slate-600 truncate">
                  {e.name}
                  {e.isAlt && <span className="text-[10px] text-slate-400"> · asıl: {e.origCode}</span>}
                </div>
                <div className="col-span-2 text-right font-medium">{e.quantity} {e.unit}</div>
              </div>
              );
            })}
          </div>

          <div className="grid grid-cols-3 gap-2 pt-1">
            <Button
              data-testid="order-whatsapp-btn"
              onClick={() => submit("whatsapp")}
              disabled={!!busy || !supplierId}
              className="h-11 gap-1.5 bg-[#25D366] hover:bg-[#128C7E] text-white"
            >
              {busy === "whatsapp" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} WhatsApp
            </Button>
            <Button
              data-testid="order-mail-btn"
              onClick={() => submit("mail")}
              disabled={!!busy || !supplierId}
              variant="outline"
              className="h-11 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              {busy === "mail" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Mail
            </Button>
            <Button
              data-testid="order-b2b-btn"
              onClick={() => submit("b2b")}
              disabled={!!busy || !supplierId}
              variant="outline"
              className="h-11 gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              {busy === "b2b" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} B2B
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
